import type { Tag } from '../types/ugla-types';

/**
 * Gets the value of a tag by its key.
 * @param key - The tag key to search for
 * @param tags - Array of tags to search in
 * @param defaultValue - Optional default value if tag is not found
 * @returns The tag value or the default value
 */
export const getTag = (
  key: string,
  tags: Tag[] | undefined,
  defaultValue?: string
): string | undefined => {
  if (!tags || tags.length === 0) {
    return defaultValue;
  }

  const tag = tags.find((tag) => tag.key === key);
  return tag?.value ?? defaultValue;
};

/**
 * Gets the product_layer tag value from an asset.
 * @param asset - The asset to extract the product_layer from
 * @returns The product_layer value or undefined
 */
export const getProductLayer = (asset: { tags?: Tag[] }): string | undefined => {
  return getTag('product_layer', asset.tags);
};

/**
 * Gets the product_ref tag value from an asset.
 * @param asset - The asset to extract the product_ref from
 * @returns The product_ref value or undefined
 */
export const getProductRef = (asset: { tags?: Tag[] }): string | undefined => {
  return getTag('product_ref', asset.tags);
};

/**
 * Gets the view tag value from an asset.
 * @param asset - The asset to extract the view from
 * @returns The view value or undefined
 */
export const getProductView = (asset: { tags?: Tag[] }): string | undefined => {
  return getTag('view', asset.tags);
};

/**
 * Checks if an asset has both product_layer and product_ref tags.
 * These tags are required for an asset to be controllable in VAP.
 * @param asset - The asset to check
 * @returns True if the asset has both required tags
 */
export const hasRequiredTags = (asset: { tags?: Tag[] }): boolean => {
  const productView = getProductView(asset);
  const productLayer = getProductLayer(asset);
  const productRef = getProductRef(asset);
  return productLayer !== undefined && productRef !== undefined && productView !== undefined;
};

/**
 * Gets all unique tag values for a given tag key from an array of assets.
 * @param assets - Array of assets to extract tag values from
 * @param tagKey - The tag key to extract values for
 * @returns Array of unique tag values
 */
export const getUniqueTagValues = (
  assets: Array<{ tags?: Tag[] }>,
  tagKey: string
): string[] => {
  const values = new Set<string>();

  assets.forEach((asset) => {
    const value = getTag(tagKey, asset.tags);
    if (value !== undefined) {
      values.add(value);
    }
  });

  return Array.from(values).sort();
};
